import { OnboardingProgress } from "@/components/OnboardingProgress";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, fileUrl, INTEREST_TAGS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, Heart, Plus, X } from "lucide-react";

export default function ProfileSetup() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState(user?.first_name || (user?.name || "").split(" ")[0] || "");
  const [surname, setSurname] = useState(user?.surname || "");
  const photoInput = useRef(null);

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/profile/photo", fd);
      await refresh();
      toast.success("Photo submitted for admin approval");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    }
  };
  const [children, setChildren] = useState([]);
  const [interests, setInterests] = useState([]);
  const [customTag, setCustomTag] = useState("");
  const [about, setAbout] = useState("");
  const [saving, setSaving] = useState(false);

  const syncCount = (n) => {
    const c = Math.max(0, Math.min(12, n || 0));
    setChildren((prev) => Array.from({ length: c }, (_, i) => prev[i] || { age: "", gender: "" }));
  };

  const setChild = (i, key, val) => {
    const arr = [...children];
    arr[i] = { ...arr[i], [key]: val };
    setChildren(arr);
  };

  const toggleTag = (t) =>
    setInterests((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const addCustom = () => {
    const t = customTag.trim();
    if (t && !interests.map((x) => x.toLowerCase()).includes(t.toLowerCase())) {
      setInterests([...interests, t]);
    }
    setCustomTag("");
  };

  const finish = async (skip) => {
    if (!skip && !firstName.trim()) {
      toast.error("First name is required");
      return;
    }
    setSaving(true);
    try {
      await api.put("/profile", {
        first_name: firstName,
        surname,
        children: skip ? [] : children.filter((c) => String(c.age).trim()),
        interests: skip ? [] : interests,
        about_family: skip ? "" : about,
      });
      await refresh();
      toast.success(skip ? "You can complete the rest anytime." : "Profile saved!");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(errMsg(e, "Could not save profile"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-12 grain" data-testid="profile-setup-page">
      <div className="bg-white/80 border border-sage/20 rounded-3xl p-8 sm:p-10 max-w-2xl w-full shadow-sm fade-in">
        <OnboardingProgress />
        <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">One last step</p>
        <h1 className="text-3xl font-black text-forest mt-1 mb-2">Tell us about your family</h1>
        <p className="text-forest/60 text-sm mb-8">This helps other families see who they're meeting at events and meet-ups.</p>

        <div className="space-y-8">
          <div className="flex items-start gap-4">
            <div className="shrink-0 text-center">
              {user?.photo_status === "approved" && user?.photo_path ? (
                <img src={fileUrl(user.photo_path)} alt="Profile" className="w-16 h-16 rounded-full object-cover border border-sage/30" data-testid="setup-photo-preview" />
              ) : (
                <img src="/logo.png" alt="Compass Kids logo" className="w-16 h-16 rounded-full object-cover border border-sage/30 bg-white" data-testid="setup-photo-placeholder" />
              )}
              <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files[0])} data-testid="setup-photo-input" />
              <button type="button" onClick={() => photoInput.current?.click()} className="text-[11px] font-bold text-sage-dark hover:underline mt-1.5" data-testid="setup-photo-btn">
                {user?.photo_status === "pending" ? "Awaiting approval — replace" : user?.photo_status === "rejected" ? "Try a new photo" : (user?.photo_status === "approved" && user?.photo_path) ? "Change" : "Add photo"}
              </button>
              {user?.photo_status === "pending" && (
                <p className="text-[10px] text-forest/50 mt-1 max-w-[9rem]" data-testid="setup-photo-pending">Your photo is pending admin approval.</p>
              )}
              {user?.photo_status === "rejected" && (
                <p className="text-[10px] text-terracotta-dark mt-1 max-w-[9rem]" data-testid="setup-photo-rejected">
                  Photo not approved{user?.photo_rejection_reason ? `: ${user.photo_rejection_reason}` : ""}. Upload a new one.
                </p>
              )}
            </div>
            <div className="flex-1 grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-forest/80">First name *</Label>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={40}
                  className="mt-1 rounded-xl bg-softstone/60 border-sage/30" data-testid="setup-first-name-input" />
                <p className="text-[10px] text-forest/40 mt-1">Shown publicly on RSVPs</p>
              </div>
              <div>
                <Label className="text-forest/80">Surname</Label>
                <Input value={surname} onChange={(e) => setSurname(e.target.value)} maxLength={40}
                  className="mt-1 rounded-xl bg-softstone/60 border-sage/30" data-testid="setup-surname-input" />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-forest/80 flex items-center gap-2 mb-3"><Users className="w-4 h-4 text-sage-dark" /> How many children do you have?</Label>
            <Input type="number" min={0} max={12} value={children.length || ""}
              onChange={(e) => syncCount(parseInt(e.target.value))}
              className="w-28 rounded-xl bg-softstone/60 border-sage/30" data-testid="children-count-input" />
            <div className="space-y-2 mt-3" data-testid="children-rows">
              {children.map((c, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input value={c.age} onChange={(e) => setChild(i, "age", e.target.value)}
                    placeholder={`Child ${i + 1} age`} className="rounded-xl bg-softstone/60 border-sage/30" data-testid={`setup-child-age-${i}`} />
                  <Select value={c.gender} onValueChange={(v) => setChild(i, "gender", v)}>
                    <SelectTrigger className="rounded-xl bg-softstone/60 border-sage/30" data-testid={`setup-child-gender-${i}`}>
                      <SelectValue placeholder="Gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-forest/80 flex items-center gap-2 mb-3"><Heart className="w-4 h-4 text-terracotta-dark" /> Family interests</Label>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="interest-presets">
              {INTEREST_TAGS.map((t) => (
                <button key={t} type="button" onClick={() => toggleTag(t)}
                  className={`text-xs font-bold px-3.5 py-2 rounded-full border transition-colors ${interests.includes(t) ? "bg-sage text-white border-sage" : "bg-white text-forest/70 border-sage/30 hover:border-sage"}`}
                  data-testid={`interest-${t.toLowerCase()}`}>
                  {t}
                </button>
              ))}
            </div>
            {interests.filter((t) => !INTEREST_TAGS.includes(t)).length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3" data-testid="custom-tags">
                {interests.filter((t) => !INTEREST_TAGS.includes(t)).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs font-bold px-3 py-2 rounded-full bg-terracotta-light text-terracotta-dark">
                    {t}
                    <button type="button" onClick={() => toggleTag(t)} aria-label={`Remove ${t}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input value={customTag} onChange={(e) => setCustomTag(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
                placeholder="Add your own (e.g. birdwatching)" maxLength={30}
                className="rounded-full bg-softstone/60 border-sage/30" data-testid="custom-interest-input" />
              <Button type="button" variant="outline" size="sm" onClick={addCustom} disabled={!customTag.trim()}
                className="rounded-full shrink-0" data-testid="add-custom-interest-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-forest/80">About our family <span className="text-forest/40 font-normal">(optional)</span></Label>
            <Textarea value={about} onChange={(e) => setAbout(e.target.value)} rows={3} maxLength={500}
              placeholder="We're a nature-loving family new to homeschooling..."
              className="mt-2 rounded-xl bg-softstone/60 border-sage/30" data-testid="about-family-input" />
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <Button onClick={() => finish(false)} disabled={saving}
              className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white shadow-md px-8" data-testid="profile-save-btn">
              {saving ? "Saving..." : "Save & continue"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
